import { useState, useEffect } from 'react'
import { enqueueSnackbar } from 'notistack'
import { format } from 'date-fns'
import { useTheme } from '@mui/material/styles'
import { Stack, Typography, useMediaQuery, Link, Button } from '@mui/material'
import ReceiptIcon from '@mui/icons-material/Receipt'

import { Types } from 'service-api'
import { captureError } from 'service-api/src/shared'
import { SortOrder } from 'service-api/src/types'

import { HeadCell, Order, SharedTable } from 'table'
import { findLocationUserProfileMembership, membershipCycleForDate } from '@shared/memberships'
import { getBenefitOfferLabel } from './helper'
import { buildErrorMessage } from '@shared/error'
import { i18WithParams as t } from '@shared/locales'
import { formatISOStringToMonthShort, getTzDateInUtc } from '@shared/dates'
import { BenefitConsumptionTableFilters } from './benefit_consumption_table_filters'
import { formatInTimeZone } from 'date-fns-tz'
import { BenefitConsumption } from 'benefit_consumption/benefit_consumption'
import { isDevEnv } from '@shared/features'

interface RequestOptions {
  sort?: string
  sortOrder?: SortOrder
  locationUserProfileId?: string
  invoiceStartDate?: string
  invoiceEndDate?: string
  benefitOffer?: string
  limit?: number
  status?: string
  nextToken?: string | null
}

const ROWS_PER_PAGE = [10, 25, 50, 100]
const BENEFIT = 'benefitId'
const MEMBERSHIP = 'membershipId'
const LOCATION = 'location'
const VISIT_DATE = 'invoiceStartDate'
const MEMBERSHIP_CYCLE = 'membershipCycle'
const ACTIONS = 'actions'

export const BenefitConsumptionTable = ({
  locationUserProfile,
  timezone,
  onServiceFetch,
  inlineFilters,
  isSortable,
  isAdmin,
  invoicePath,
  isMembershipActive,
  currentBenefitsUsage,
  currentBenefits,
  hasSharedCRM,
}: {
  locationUserProfile: Types.LocationUserProfileAdminDto | Types.LocationUserProfileClientDto
  timezone: string
  onServiceFetch?: ({
    locationUserProfileId,
    options,
  }: {
    locationUserProfileId: string
    options: RequestOptions
  }) => Promise<Types.APIBenefitConsumptionRecordAdminDtoResponse>
  inlineFilters?: boolean
  isSortable?: boolean
  isAdmin?: boolean
  invoicePath?: (benefitConsumptionRecord: Types.BenefitConsumptionRecordAdminDto) => string
  isMembershipActive?: boolean
  currentBenefitsUsage?: Types.BenefitConsumptionRecordAdminDto[] | Types.BenefitConsumptionRecordClientDto[]
  currentBenefits?: Types.BenefitAdminDto[]
  hasSharedCRM?: boolean
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [benefitConsumptionRecords, setBenefitConsumptionRecords] =
    useState<Types.APIBenefitConsumptionRecordAdminDtoResponse>()
  const [visibleBenefitConsumptionRecords, setVisibleBenefitConsumptionRecords] =
    useState<Types.BenefitConsumptionRecordAdminDto[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [shouldResetPage, setShouldResetPage] = useState(false)
  const [requestOptions, setRequestOptions] = useState<RequestOptions>({
    sort: VISIT_DATE,
    sortOrder: SortOrder.DESC,
    status: 'ACTIVE',
    limit: 50,
  })

  const locationUserProfileMembership =
    findLocationUserProfileMembership(locationUserProfile?.locationUserProfileMemberships, true) || {}
  const isMembershipCancelled = locationUserProfileMembership?.subStatus === 'CANCELED'

  const fetchRecords = async (options: RequestOptions = {}) => {
    try {
      setShouldResetPage(true)
      setIsLoading(true)
      const filterOptions = { ...requestOptions, ...options }
      let allResults: Types.BenefitConsumptionRecordAdminDto[] = []
      let nextToken: string | null | undefined = undefined
      do {
        const currentOptions: RequestOptions = { ...filterOptions, nextToken }
        const response = await onServiceFetch?.({
          locationUserProfileId: locationUserProfile?.id || '',
          options: currentOptions,
        })
        if (response) {
          allResults = [...allResults, ...response.results]
          nextToken = response.nextToken
        }
      } while (nextToken)

      setBenefitConsumptionRecords({
        results: allResults,
        nextToken: null,
      })

      let visibleResults = allResults
      if (options.benefitOffer && options.benefitOffer !== t('global.label.all')) {
        visibleResults = allResults.filter(
          (r) => getBenefitOfferLabel(r.benefit!, r.offerId || '') === options.benefitOffer
        )
      }

      sortResults(requestOptions.sort || VISIT_DATE, requestOptions.sortOrder || SortOrder.DESC, visibleResults)
    } catch (err) {
      enqueueSnackbar(buildErrorMessage(err), { variant: 'error' })
      captureError(err as Error)
    } finally {
      setShouldResetPage(false)
      setIsLoading(false)
    }
  }

  const applyFilters = (filters: { benefitOffer?: string; startDate?: Date; endDate?: Date }) => {
    const options: RequestOptions = {
      benefitOffer: filters.benefitOffer,
    }
    if (filters.startDate && filters.endDate) {
      options.invoiceStartDate = `gte_${format(filters.startDate, 'yyyy-MM-dd')}_lte_${format(
        filters.endDate,
        'yyyy-MM-dd'
      )}`
    } else if (filters.startDate) {
      options.invoiceStartDate = format(filters.startDate, 'yyyy-MM-dd')
    }
    fetchRecords(options)
  }

  const clearFilters = () => {
    const options = {
      sort: VISIT_DATE,
      sortOrder: SortOrder.DESC,
    }
    fetchRecords(options)
  }

  const sortResults = (sort: string, sortOrder: Order, results?: Types.BenefitConsumptionRecordAdminDto[]) => {
    const sortedResults = (results || benefitConsumptionRecords?.results || []).sort((a, b) => {
      if (sort === BENEFIT) {
        const benefitOfferLabelA = getBenefitOfferLabel(a.benefit!, a.offerId || '')
        const benefitOfferLabelB = getBenefitOfferLabel(b.benefit!, b.offerId || '')
        return sortOrder === SortOrder.DESC
          ? benefitOfferLabelB.localeCompare(benefitOfferLabelA)
          : benefitOfferLabelA.localeCompare(benefitOfferLabelB)
      } else if (sort === VISIT_DATE) {
        return sortOrder === SortOrder.DESC
          ? new Date(b.invoiceStartDate!).getTime() - new Date(a.invoiceStartDate!).getTime()
          : new Date(a.invoiceStartDate!).getTime() - new Date(b.invoiceStartDate!).getTime()
      }
      return 0
    })
    setVisibleBenefitConsumptionRecords(sortedResults)
  }

  const handleSorting = (sort: string, order: Order) => {
    setRequestOptions({ ...requestOptions, sort, sortOrder: order as SortOrder })
    sortResults(sort, order)
  }

  useEffect(() => {
    fetchRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headCells = (): HeadCell[] => {
    return [
      {
        id: BENEFIT,
        label: t('global.label.benefit'),
        width: '25%',
        sortable: isSortable,
      },
      ...(isAdmin
        ? [
            {
              id: MEMBERSHIP,
              label: t('global.label.membership'),
              width: '25%',
              sortable: false,
            },
            ...(hasSharedCRM
              ? [
                  {
                    id: LOCATION,
                    label: t('global.label.location'),
                    width: '25%',
                    sortable: false,
                  },
                ]
              : []),
          ]
        : []),
      {
        id: VISIT_DATE,
        label: t('membership.benefit.visitDate'),
        width: '25%',
        sortable: isSortable,
      },
      {
        id: MEMBERSHIP_CYCLE,
        label: t('membership.benefit.membershipCycle'),
        width: '25%',
        sortable: false,
      },
      {
        id: ACTIONS,
        label: t('global.label.actions'),
        align: 'right',
        width: '25%',
        sortable: false,
      },
    ]
  }

  const getRenderRows = (benefitConsumptionRecord: Types.BenefitConsumptionRecordAdminDto) => {
    const { benefit, offerId } = benefitConsumptionRecord
    const { startDate: membershipCycleStartDate, endDate: membershipCycleEndDate } =
      membershipCycleForDate(
        benefitConsumptionRecord.locationUserProfileMembership,
        benefitConsumptionRecord.locationUserProfileMembership?.membership?.billingCycle,
        timezone,
        getTzDateInUtc(benefitConsumptionRecord.invoiceStartDate, null, timezone)
      ) || {}

    const rowData: Record<string, React.ReactNode> = {
      [BENEFIT]: (
        <Typography variant="body2bold">
          {getBenefitOfferLabel(benefit!, offerId || '')} {t('membership.benefit.discount.small')}
        </Typography>
      ),
      [VISIT_DATE]: (
        <Typography variant="body2bold">
          {formatISOStringToMonthShort(benefitConsumptionRecord.invoiceStartDate!)}
        </Typography>
      ),
      [MEMBERSHIP_CYCLE]: (
        <Typography variant="body2bold">
          {membershipCycleStartDate && membershipCycleEndDate
            ? t('global.label.dateRange', {
                startDate: formatInTimeZone(membershipCycleStartDate, timezone, 'MMM d, yyyy'),
                endDate: formatInTimeZone(membershipCycleEndDate, timezone, 'MMM d, yyyy'),
              })
            : ''}
        </Typography>
      ),
      [ACTIONS]: (
        <Stack direction="row" spacing={1} justifyContent="right">
          <Link
            href={invoicePath?.(benefitConsumptionRecord)}
            component={Button}
            sx={{ fontSize: 14, marginTop: '3px', minWidth: 28, textDecoration: 'none' }}
          >
            <ReceiptIcon />
          </Link>
        </Stack>
      ),
    }

    if (isAdmin) {
      rowData[MEMBERSHIP] = (
        <Typography variant="body2bold">
          {benefitConsumptionRecord.locationUserProfileMembership?.membership?.displayName || ''}
        </Typography>
      )
      if (hasSharedCRM) {
        rowData[LOCATION] = (
          <Typography variant="body2bold">
            {benefitConsumptionRecord.location?.displayName || benefitConsumptionRecord.location?.name || ''}
          </Typography>
        )
      }
    }

    return rowData
  }

  return (
    <Stack>
      {!inlineFilters && (
        <Stack>
          <BenefitConsumptionTableFilters
            benefitConsumptionRecords={benefitConsumptionRecords}
            onApplyFilters={applyFilters}
            onClearFilters={clearFilters}
            hideBenefitFilter={isMembershipCancelled}
          />
        </Stack>
      )}
      <Stack
        sx={{
          p: 3,
          border: '1px solid #E0E0E0',
        }}
      >
        <Typography variant="h5">{t('membership.benefit.tableLabel')}</Typography>
        {isDevEnv() && isMembershipActive && (
          <BenefitConsumption currentBenefitsUsage={currentBenefitsUsage} currentBenefits={currentBenefits} />
        )}
        <SharedTable
          containerStyle={{
            '&&&': {
              padding: 0,
            },
          }}
          component={Stack}
          rows={visibleBenefitConsumptionRecords?.map(getRenderRows) || []}
          head={headCells()}
          defaultOrder="desc"
          defaultOrderBy={VISIT_DATE}
          isLoading={isLoading}
          rowsPerPageConfig={ROWS_PER_PAGE}
          hasMore={false}
          emptyTableMessage={t('global.label.noData')}
          onSort={handleSorting}
          shouldResetPage={shouldResetPage}
          isScrollable={isMobile}
          dropdownFilters={
            inlineFilters ? (
              <BenefitConsumptionTableFilters
                benefitConsumptionRecords={benefitConsumptionRecords}
                onApplyFilters={applyFilters}
                onClearFilters={clearFilters}
                hideBenefitFilter={isMembershipCancelled}
                inline={true}
              />
            ) : undefined
          }
          dropdownFiltersStyle={inlineFilters ? { backgroundColor: 'transparent', mt: 2, mb: 3 } : undefined}
        />
      </Stack>
    </Stack>
  )
}
